import {
    Divider,
    Flex,
    HStack,
    Image,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    useDisclosure,
    useTheme,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import apiClient from '../../../../canopy-ui-components/api';
import CanopyButton from '../../../../canopy-ui-components/components/CanopyButton/CanopyButton';
import CanopyIconButton from '../../../../canopy-ui-components/components/IconButton/IconButton';
import * as ga from '../../../../canopy-ui-components/google-analytics';
import { useCurrentColors } from '../../../../canopy-ui-components/hooks/UseCurrentColors/UseCurrentColors';
function DeleteFileModal({
    userId,
    data,
    showModal,
    setShowModal,
    globalProps,
    refreshData,
}: {
    userId?: number[];
    data?: any[];
    showModal: boolean;
    setShowModal: (flag: boolean) => void;
    globalProps?: GlobalProps;
    refreshData?: (args?: any) => void;
}) {
    const currentColors = useCurrentColors();
    const { t } = globalProps || {};
    const [isLoading, setIsLoading] = useState(false);
    const [inputFiles, setInputFiles] = useState<Record<string, any>[]>([]);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const theme = useTheme();

    const handleConfirmDelete = async () => {
        setIsLoading(true);
        const response = await Promise.all(
            inputFiles?.map(async (file: any) => {
                try {
                    const { data }: any = await apiClient.delete('documents', {
                        queryParams: {
                            document_id: file.documentId,
                        },
                    });

                    setIsLoading(false);
                    setShowModal(false);

                    return data;
                } catch (e) {
                    setIsLoading(false);
                    setShowModal(false);
                    return { success: false };
                }
            })
        );
        let toastInfo = {};
        if (!response?.find((status) => status?.success === false)) {
            toastInfo = {
                title: t('ui.mm.document-delete-success', {
                    count: inputFiles?.length,
                }),
                backgroundColor: currentColors.green['500'],
            };
        } else {
            toastInfo = {
                title: t('ui.mm.document-delete-error', {
                    count: inputFiles?.length,
                }),
                backgroundColor: currentColors.errorText,
            };
        }
        setShowModal(false);
        refreshData &&
            refreshData({
                ...toastInfo,
            });
    };

    const handleOnClose = () => {
        ga.event({
            action: 'Portfolio > Documents > Close Delete Document',
        });
        setShowModal(false);
    };

    useEffect(() => {
        showModal ? onOpen() : onClose();
    }, [showModal]);

    useEffect(() => {
        const inputFiles = [...(data || [])]?.map((file) => {
            return {
                documentId: file.documentId,
                name: file.fileName,
                size: '',
                description: file?.description || '',
                password: file.filePassword || '',
                status: 'ready-to-update',
                message: '',
                transactionTags:
                    file?.transactionTagValue?.map(
                        (item: any) => item.tagValue
                    ) || [],
                securityTags:
                    file?.securityTagValue?.map((item: any) => item.tagValue) ||
                    [],
                customTags:
                    file?.customTagValue?.map((item: any) => item.tagValue) ||
                    [],
                keyBoardInputMode: false,
                enabledTags: ['transactionTags', 'securityTags', 'customTags'],
            };
        });
        setInputFiles(inputFiles);
    }, [data]);

    return (
        <Modal
            isCentered
            closeOnOverlayClick={false}
            onEsc={() => setShowModal(false)}
            isOpen={isOpen}
            size="2xl"
            onClose={handleOnClose}>
            <ModalOverlay
                style={{
                    backdropFilter: 'blur(5px)',
                }}
            />
            <ModalContent>
                <ModalHeader
                    color={currentColors.brand['10']}
                    fontSize="20px"
                    fontWeight="bold"
                    padding="15px">
                    <Flex alignItems="center">
                        <Image
                            src={theme?.images?.headerLogo}
                            maxWidth="100%"
                            width="auto"
                            height={'35px'}
                            mr="17.5px"
                        />
                        <Text width="100%">
                            {t('ui.mm.delete-file', {
                                count: inputFiles?.length,
                            })}
                        </Text>
                    </Flex>
                    <Divider
                        borderColor={currentColors.brand['700']}
                        width="100%"
                        mb="0px"
                    />
                </ModalHeader>
                <ModalCloseButton
                    as={() => (
                        <CanopyIconButton
                            background="transparent"
                            cursor="pointer"
                            border="none"
                            display="inline-flex"
                            onClick={handleOnClose}
                            color={currentColors.brand['500']}
                            width="24px"
                            height="24px"
                            aria-label="remove"
                            icon="ActClose"
                            variant="ghost"
                            position="absolute"
                            top="15px"
                            right="15px"
                            isDisabled={isLoading}
                        />
                    )}
                />
                <ModalBody
                    padding="0 15px"
                    maxHeight="60vh"
                    overflow="auto">
                    <Text>
                        {t('ui.mm.confirm-file-delete', {
                            count: inputFiles?.length,
                        })}
                    </Text>
                </ModalBody>

                {inputFiles?.length ? (
                    <ModalFooter
                        flexDirection="column"
                        p="15px">
                        <Divider
                            mt="0"
                            mb="15px"
                        />
                        <HStack
                            width="100%"
                            spacing="15px"
                            justifyContent="flex-end">
                            <CanopyButton
                                fontSize="sm"
                                width="100px"
                                fontWeight="bold"
                                variant="outline"
                                isDisabled={isLoading}
                                onClick={handleOnClose}>
                                {t('cancel')}
                            </CanopyButton>
                            <CanopyButton
                                data-test="deleteButton"
                                fontSize="sm"
                                width="100px"
                                fontWeight="bold"
                                isDisabled={isLoading}
                                isLoading={isLoading}
                                onClick={handleConfirmDelete}>
                                {t('delete')}
                            </CanopyButton>
                        </HStack>
                    </ModalFooter>
                ) : null}
            </ModalContent>
        </Modal>
    );
}

export default DeleteFileModal;
